// RAG (Retrieval-Augmented Generation) 시스템 서비스
// LangChain + Claude API 조합으로 구현

export interface DocumentChunk {
  id: string;
  content: string;
  metadata: {
    source: string;
    title: string;
    page?: number;
    timestamp: Date;
    tags: string[];
  };
  embedding?: number[];
}

export interface SearchResult {
  chunk: DocumentChunk;
  score: number;
  relevance: string;
}

export interface RAGResponse {
  answer: string;
  sources: SearchResult[];
  confidence: number;
  processingTime: number;
}

export interface PortfolioAnalysis {
  summary: string;
  keyPoints: string[];
  recommendations: string[];
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

class RAGService {
  private baseUrl: string;

  constructor() {
    // API URL 동적 감지 (환경 변수 무시)
    const getApiBaseUrl = (): string => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
      const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
      if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return `${protocol}//${hostname}:8001`;
      }
      return 'http://localhost:8001';
    };
    this.baseUrl = getApiBaseUrl();
  }

  private getAuthHeaders(contentType?: string): HeadersInit {
    const token = localStorage.getItem('proposalcraft_access_token');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  }

  // 문서 업로드 및 청킹
  async uploadDocument(file: File): Promise<{ success: boolean; documentId: string; chunks: number }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${this.baseUrl}/api/rag/upload`, {
        method: 'POST',
        body: formData,
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('문서 업로드 실패');
      }

      const result = await response.json();
      return {
        success: true,
        documentId: result.document_id,
        chunks: result.chunks_count
      };
    } catch (error) {
      console.error('문서 업로드 오류:', error);
      return {
        success: false,
        documentId: '',
        chunks: 0
      };
    }
  }

  // 문서 검색 (벡터 유사도 기반)
  async searchDocuments(query: string, limit: number = 5): Promise<SearchResult[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/search`, {
        method: 'POST',
        headers: this.getAuthHeaders('application/json'),
        body: JSON.stringify({
          query,
          limit,
        })
      });

      if (!response.ok) {
        throw new Error('문서 검색 실패');
      }

      const results = await response.json();
      return results.search_results;
    } catch (error) {
      console.error('문서 검색 오류:', error);
      return [];
    }
  }

  // RAG 기반 질의응답
  async askQuestion(question: string, contextDocuments?: string[]): Promise<RAGResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/ask`, {
        method: 'POST',
        headers: this.getAuthHeaders('application/json'),
        body: JSON.stringify({
          question,
          context_documents: contextDocuments,
        })
      });

      if (!response.ok) {
        throw new Error('질의응답 실패');
      }

      const result = await response.json();
      return result.response;
    } catch (error) {
      console.error('질의응답 오류:', error);
      return {
        answer: '죄송합니다. 질문을 처리하는 중 오류가 발생했습니다.',
        sources: [],
        confidence: 0,
        processingTime: 0
      };
    }
  }

  // 포트폴리오 AI 분석
  async analyzePortfolio(documentIds: string[]): Promise<PortfolioAnalysis> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/analyze-portfolio`, {
        method: 'POST',
        headers: this.getAuthHeaders('application/json'),
        body: JSON.stringify({
          document_ids: documentIds,
        })
      });

      if (!response.ok) {
        throw new Error('포트폴리오 분석 실패');
      }

      const result = await response.json();
      return result.analysis;
    } catch (error) {
      console.error('포트폴리오 분석 오류:', error);
      return {
        summary: '포트폴리오 분석을 완료할 수 없습니다.',
        keyPoints: [],
        recommendations: [],
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: []
      };
    }
  }

  // 문서 태그 자동 생성
  async generateTags(content: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/generate-tags`, {
        method: 'POST',
        headers: this.getAuthHeaders('application/json'),
        body: JSON.stringify({
          content,
        })
      });

      if (!response.ok) {
        throw new Error('태그 생성 실패');
      }

      const result = await response.json();
      return result.tags;
    } catch (error) {
      console.error('태그 생성 오류:', error);
      return [];
    }
  }

  // 문서 요약 생성
  async generateSummary(content: string, maxLength: number = 200): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/summarize`, {
        method: 'POST',
        headers: this.getAuthHeaders('application/json'),
        body: JSON.stringify({
          content,
          max_length: maxLength,
        })
      });

      if (!response.ok) {
        throw new Error('요약 생성 실패');
      }

      const result = await response.json();
      return result.summary;
    } catch (error) {
      console.error('요약 생성 오류:', error);
      return '요약을 생성할 수 없습니다.';
    }
  }

  // 벡터 데이터베이스 상태 확인
  async getDatabaseStatus(): Promise<{
    totalDocuments: number;
    totalChunks: number;
    lastUpdate: string;
    health: 'healthy' | 'warning' | 'error';
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/status`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('상태 확인 실패');
      }

      const result = await response.json();
      return result.status;
    } catch (error) {
      console.error('상태 확인 오류:', error);
      return {
        totalDocuments: 0,
        totalChunks: 0,
        lastUpdate: new Date().toISOString(),
        health: 'error'
      };
    }
  }

  // 문서 삭제
  async deleteDocument(documentId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/rag/documents/${documentId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      return response.ok;
    } catch (error) {
      console.error('문서 삭제 오류:', error);
      return false;
    }
  }

  // 배치 문서 처리
  async processBatchDocuments(files: File[]): Promise<{
    success: number;
    failed: number;
    results: Array<{ fileName: string; success: boolean; documentId?: string; error?: string }>;
  }> {
    const results = [];
    let success = 0;
    let failed = 0;

    for (const file of files) {
      try {
        const result = await this.uploadDocument(file);
        if (result.success) {
          success++;
          results.push({
            fileName: file.name,
            success: true,
            documentId: result.documentId
          });
        } else {
          failed++;
          results.push({
            fileName: file.name,
            success: false,
            error: '업로드 실패'
          });
        }
      } catch (error) {
        failed++;
        results.push({
          fileName: file.name,
          success: false,
          error: error instanceof Error ? error.message : '알 수 없는 오류'
        });
      }
    }

    return { success, failed, results };
  }
}

// 싱글톤 인스턴스 생성
export const ragService = new RAGService();

// 사용 예시:
/*
// 1. 문서 업로드
const uploadResult = await ragService.uploadDocument(file);
if (uploadResult.success) {
  console.log(`문서가 ${uploadResult.chunks}개 청크로 분할되어 업로드되었습니다.`);
}

// 2. 질의응답
const answer = await ragService.askQuestion("포트폴리오의 주요 강점은 무엇인가요?");
console.log(answer.answer);

// 3. 포트폴리오 분석
const analysis = await ragService.analyzePortfolio(['doc1', 'doc2']);
console.log(analysis.summary);
*/
